/**
 * Unified Report Import Step
 * 
 * Single screen with:
 * 1. Provider selection
 * 2. Credential entry
 * 3. Connect & Import button
 * 4. Manual upload fallback
 * 
 * Handles import failure with modal recovery options.
 */

import React, { useState } from 'react';
import { flushSync } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  ArrowRight,
  Shield, 
  Lock, 
  Eye, 
  EyeOff, 
  AlertCircle,
  Upload,
  RefreshCw,
  Check,
  Loader2,
  FileText,
  ExternalLink,
  X,
} from 'lucide-react';
import OnboardingCard, { OnboardingButton, OnboardingInput, OnboardingHeading } from '../components/OnboardingCard';
import { 
  getActiveProviders, 
  renderProviderLogo, 
  getProviderDisplayInfo,
  getCreateAccountUrl,
  getCreateAccountCta,
  getProviderFormConfig,
} from '../../../config/providerRegistry';
import { toast } from 'sonner';
import {
  validateReportFile,
  CREDIT_REPORT_SUPPORTED_PROVIDERS_LINE,
} from '../../../utils/fileUploadValidation';
import {
  providerRequiresVerificationField,
  getSensitiveCredentialFieldsToClear,
} from '../../../config/providerCapabilities';
import ImportReportBureauLogos from '../../../components/import/ImportReportBureauLogos';

// Get providers from centralized registry
const PROVIDERS = getActiveProviders().map(p => ({
  id: p.provider_key,
  name: p.provider_name,
  active: p.supported_import_flow,
  description: p.provider_description,
  badge: p.badge_text,
  create_account_url: p.create_account_url,
  create_account_cta: p.create_account_cta,
  form_fields: p.form_fields,
}));

// Import failure modal component
const ImportFailureModal = ({ 
  isOpen, 
  onClose, 
  onRetry, 
  onUpdateCredentials, 
  onManualUpload, 
  onSkipToHome,
  errorDetails 
}) => {
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-stone-800 rounded-2xl max-w-md w-full shadow-2xl border border-slate-700"
          data-testid="import-failure-modal"
        >
          {/* Header */}
          <div className="p-6 border-b border-slate-700">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                <AlertCircle size={24} className="text-red-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">We couldn't import your report</h2>
                <p className="text-slate-400 text-sm mt-1">
                  We were unable to connect to your credit monitoring account or retrieve your report.
                </p>
              </div>
            </div>
          </div>
          
          {/* Body */}
          <div className="p-6">
            <p className="text-slate-300 text-sm mb-4">
              This can happen because of:
            </p>
            <ul className="text-slate-400 text-sm space-y-2 mb-6">
              <li className="flex items-start gap-2">
                <span className="text-slate-500">•</span>
                <span>Incorrect login details</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-slate-500">•</span>
                <span>Provider security checks</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-slate-500">•</span>
                <span>Temporary provider issues</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-slate-500">•</span>
                <span>Missing report access on your account</span>
              </li>
            </ul>
            
            {/* Error details if available */}
            {errorDetails && (
              <div className="bg-stone-900/50 rounded-lg p-3 mb-6 text-xs text-slate-500">
                <span className="font-medium">Details:</span> {errorDetails}
              </div>
            )}
            
            {/* Action buttons */}
            <div className="space-y-3">
              <button
                type="button"
                onClick={onRetry}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-xl transition-colors"
                data-testid="retry-import-btn"
              >
                <RefreshCw size={18} />
                Try Again
              </button>
              
              <button
                type="button"
                onClick={onUpdateCredentials}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-xl transition-colors"
                data-testid="update-credentials-btn"
              >
                <Lock size={18} />
                Update Login Details
              </button>
              
              <button
                type="button"
                onClick={onManualUpload}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-xl transition-colors"
                data-testid="manual-upload-btn"
              >
                <Upload size={18} />
                Upload Report File Instead
              </button>
              
              <button
                type="button"
                onClick={onSkipToHome}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 text-slate-400 hover:text-white font-medium transition-colors"
                data-testid="skip-to-dashboard-btn"
              >
                Skip for Now and Return to Dashboard
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Manual upload — HTML export only (validated before upload)
const ManualUploadSection = ({ onFileSelect, isUploading }) => {
  const [dragOver, setDragOver] = useState(false);

  const pickFile = (file) => {
    const validation = validateReportFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }
    onFileSelect(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) pickFile(file);
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) pickFile(file);
    e.target.value = '';
  };

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      className="mt-6"
    >
      <div id="onboarding-manual-html-upload-section" className="border-t border-slate-700 pt-6">
        <h3 className="text-white font-medium mb-2 flex items-center gap-2">
          <Upload size={18} className="text-emerald-400" />
          Upload Report File
        </h3>
        <p className="text-slate-400 text-sm mb-4">
          Upload your latest credit monitoring report. PDF or HTML files accepted.
        </p>
        <p className="text-slate-500 text-xs mb-4">{CREDIT_REPORT_SUPPORTED_PROVIDERS_LINE}</p>
        <ImportReportBureauLogos isDark className="mb-4" />

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
            dragOver ? 'border-emerald-500 bg-emerald-500/10' : 'border-slate-600 hover:border-slate-500'
          }`}
        >
          {isUploading ? (
            <div className="flex flex-col items-center gap-3">
              <Loader2 size={32} className="text-emerald-400 animate-spin" />
              <p className="text-slate-300">Uploading and analyzing...</p>
            </div>
          ) : (
            <>
              <FileText size={32} className="mx-auto text-slate-500 mb-3" />
              <p className="text-slate-300 mb-2">Drag and drop your PDF or HTML report file here</p>
              <p className="text-slate-500 text-sm mb-4">or</p>
              <label className="inline-flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg cursor-pointer transition-colors">
                <Upload size={16} />
                Select Report File
                <input
                  id="onboarding-manual-html-upload"
                  type="file"
                  accept=".pdf,.html,.htm"
                  onChange={handleFileInput}
                  className="hidden"
                  data-testid="manual-upload-input"
                />
              </label>
              <p className="text-slate-500 text-xs mt-3">Files are parsed securely</p>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Main unified import step component
const UnifiedReportImportStep = ({ 
  formData, 
  updateFormData, 
  prevStep, 
  onStartImport,
  onManualUpload,
  onImportLater,
  onSkipToHome,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [importError, setImportError] = useState(null);
  const [showManualUpload, setShowManualUpload] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  
  // Internal sub-step: 'provider' or 'credentials'
  const [subStep, setSubStep] = useState('provider');

  const selectedProvider = PROVIDERS.find(p => p.id === formData.provider) || PROVIDERS[0];

  const handleProviderSelect = (providerId) => {
    updateFormData({
      provider: providerId,
      ...getSensitiveCredentialFieldsToClear(providerId),
    });
  };

  const handleContinueToCredentials = () => {
    if (formData.provider) {
      setSubStep('credentials');
    }
  };

  const handleBackToProvider = () => {
    setSubStep('provider');
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.username?.trim()) {
      newErrors.username = 'Username or email is required';
    }
    
    if (!formData.password?.trim()) {
      newErrors.password = 'Password is required';
    }
    
    if (providerRequiresVerificationField(formData.provider) && !formData.securityAnswer?.trim()) {
      newErrors.securityAnswer = 'Last 4 of SSN or security answer is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConnectAndImport = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    setImportError(null);
    
    try {
      const result = await onStartImport();
      
      if (!result.success) {
        setImportError(result.error || 'Import failed');
        setShowFailureModal(true);
      }
      // Success case is handled by parent component
    } catch (error) {
      setImportError(error.message || 'Connection failed');
      setShowFailureModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetry = () => {
    setShowFailureModal(false);
    handleConnectAndImport();
  };

  const handleUpdateCredentials = () => {
    setShowFailureModal(false);
    // Focus on password field
    const passwordInput = document.querySelector('[data-testid="password-input"]');
    if (passwordInput) passwordInput.focus();
  };

  const handleManualUploadClick = () => {
    flushSync(() => {
      setShowFailureModal(false);
      setSubStep('credentials');
      setShowManualUpload(true);
    });
    document.getElementById('onboarding-manual-html-upload-section')?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
    document.getElementById('onboarding-manual-html-upload')?.click();
  };

  const handleFileSelect = async (file) => {
    const validation = validateReportFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }
    setIsUploading(true);
    try {
      await onManualUpload(file);
    } catch (error) {
      setImportError(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSkipToHome = () => {
    setShowFailureModal(false);
    onSkipToHome();
  };

  const handleImportLater = () => {
    if (onImportLater) {
      onImportLater();
      return;
    }
    onSkipToHome();
  };

  return (
    <>
      <OnboardingCard showGlow={false}>
        <div className="p-6 sm:p-10">
          <AnimatePresence mode="wait">
            {/* SCREEN 1: Choose your provider */}
            {subStep === 'provider' && (
              <motion.div
                key="provider-screen"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {/* Header */}
                <OnboardingHeading
                  title="Choose your credit monitoring provider"
                  subtitle="Select the service where you have your credit monitoring account."
                />

                {/* Provider Grid */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="mt-8"
                >
                  <div className="grid gap-3">
                    {PROVIDERS.map((provider) => {
                      const isSelected = provider.id === formData.provider;
                      return (
                        <button
                          key={provider.id}
                          onClick={() => provider.active && handleProviderSelect(provider.id)}
                          disabled={!provider.active}
                          className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all ${
                            isSelected
                              ? 'border-emerald-500 bg-emerald-500/10'
                              : provider.active
                                ? 'border-slate-700 hover:border-slate-500 bg-slate-800/50'
                                : 'border-slate-800 bg-stone-900/30 opacity-40 cursor-not-allowed'
                          }`}
                          data-testid={`provider-${provider.id}`}
                        >
                          {/* Logo Container */}
                          <div className={`w-36 h-12 flex items-center justify-start rounded-lg px-3 shrink-0 ${
                            isSelected ? 'bg-white/15' : 'bg-white/10'
                          }`}>
                            {renderProviderLogo(provider.id, 'md')}
                          </div>
                          
                          {/* Provider Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`font-medium truncate ${isSelected ? 'text-white' : 'text-slate-200'}`}>
                                {provider.name}
                              </span>
                              {provider.badge && (
                                <span className="text-xs px-2 py-0.5 bg-emerald-600 text-white rounded shrink-0">
                                  {provider.badge}
                                </span>
                              )}
                              {!provider.active && (
                                <span className="text-xs px-2 py-0.5 bg-slate-700 text-slate-400 rounded shrink-0">
                                  Coming Soon
                                </span>
                              )}
                            </div>
                            <span className="text-slate-500 text-sm truncate block">{provider.description}</span>
                          </div>
                          
                          {/* Selection Indicator */}
                          {isSelected && provider.active && (
                            <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                              <Check size={14} className="text-white" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </motion.div>

                {/* Continue Button */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="mt-8"
                >
                  <OnboardingButton
                    onClick={handleContinueToCredentials}
                    disabled={!formData.provider}
                    className="w-full"
                    data-testid="continue-to-credentials-btn"
                  >
                    Continue with {selectedProvider.name}
                    <ArrowRight size={20} />
                  </OnboardingButton>
                </motion.div>

                {/* Back Navigation */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="mt-6 pt-6 border-t border-slate-700/30"
                >
                  <OnboardingButton
                    onClick={prevStep}
                    variant="ghost"
                    className="text-slate-400 hover:text-white"
                  >
                    <ArrowLeft size={18} />
                    Back
                  </OnboardingButton>
                </motion.div>
              </motion.div>
            )}

            {/* SCREEN 2: Enter credentials */}
            {subStep === 'credentials' && (
              <motion.div
                key="credentials-screen"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
              >
                {/* Header */}
                <OnboardingHeading
                  title={`Connect to ${selectedProvider.name}`}
                  subtitle="Enter your login credentials to securely import your credit report."
                />

                {/* Selected Provider Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                  className="mt-6 flex items-center gap-4 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30"
                >
                  <div className="w-32 h-10 flex items-center justify-start bg-white/15 rounded-lg px-2">
                    {renderProviderLogo(selectedProvider.id, 'sm')}
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium">{selectedProvider.name}</p>
                    <p className="text-slate-400 text-sm">{selectedProvider.description}</p>
                  </div>
                  <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                    <Check size={14} className="text-white" />
                  </div>
                </motion.div>

                {/* Required Credentials Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  className="mt-6 p-5 rounded-xl bg-slate-800/40 border border-slate-700/30"
                >
                  <p className="text-xs text-slate-400 uppercase tracking-wider font-medium mb-5">Login Credentials</p>
                  
                  <div className="space-y-4">
                    {/* Username */}
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        {selectedProvider.form_fields?.username_label || 'Username or Email'} <span className="text-red-400">*</span>
                      </label>
                      <OnboardingInput
                        type="text"
                        value={formData.username || ''}
                        onChange={(e) => {
                          updateFormData({ username: e.target.value });
                          if (errors.username) setErrors(prev => ({ ...prev, username: null }));
                        }}
                        placeholder={selectedProvider.form_fields?.username_placeholder || `Enter your ${selectedProvider.name} username or email`}
                        error={errors.username}
                        data-testid="username-input"
                      />
                    </div>

                    {/* Password */}
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        {selectedProvider.form_fields?.password_label || 'Password'} <span className="text-red-400">*</span>
                      </label>
                      <div className="relative">
                        <OnboardingInput
                          type={showPassword ? 'text' : 'password'}
                          value={formData.password || ''}
                          onChange={(e) => {
                            updateFormData({ password: e.target.value });
                            if (errors.password) setErrors(prev => ({ ...prev, password: null }));
                          }}
                          placeholder={selectedProvider.form_fields?.password_placeholder || 'Enter your password'}
                          error={errors.password}
                          className="pr-12"
                          data-testid="password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                        >
                          {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      </div>
                    </div>

                    {providerRequiresVerificationField(selectedProvider.id) && (
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Last 4 of SSN or Security Answer <span className="text-red-400">*</span>
                        </label>
                        <OnboardingInput
                          type="text"
                          value={formData.securityAnswer || ''}
                          onChange={(e) => {
                            updateFormData({ securityAnswer: e.target.value });
                            if (errors.securityAnswer) setErrors(prev => ({ ...prev, securityAnswer: null }));
                          }}
                          placeholder="Enter the last 4 digits of your SSN or your security answer"
                          error={errors.securityAnswer}
                          maxLength={50}
                          data-testid="security-answer-input"
                        />
                        <p className="text-xs text-slate-500 mt-1.5">
                          Required by {selectedProvider.name} for account verification
                        </p>
                      </div>
                    )}
                  </div>
                </motion.div>

                {/* Optional: Save Login Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35 }}
                  className="mt-4 p-4 rounded-xl bg-slate-800/20 border border-slate-700/20"
                >
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <div className="relative mt-0.5">
                      <input
                        type="checkbox"
                        checked={formData.saveLogin || false}
                        onChange={(e) => updateFormData({ saveLogin: e.target.checked })}
                        className="sr-only"
                        data-testid="save-login-checkbox"
                      />
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                        formData.saveLogin 
                          ? 'bg-emerald-500 border-emerald-500' 
                          : 'border-slate-600 group-hover:border-slate-500'
                      }`}>
                        {formData.saveLogin && <Check size={12} className="text-white" />}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-slate-300 font-medium">
                        Save this login securely for faster future imports
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Optional — Your credentials are encrypted and can be deleted anytime
                      </p>
                    </div>
                  </label>
                </motion.div>

                {/* Security Trust Badge */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="mt-5 p-3.5 rounded-xl bg-emerald-500/5 border border-emerald-500/20"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center shrink-0">
                      <Shield size={16} className="text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-sm text-emerald-300 font-medium">Secure Connection</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        Your credentials are encrypted end-to-end and used only to connect to your account.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Primary CTA */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.45 }}
                  className="mt-6"
                >
                  <OnboardingButton
                    onClick={handleConnectAndImport}
                    disabled={
                      isLoading
                      || !formData.username
                      || !formData.password
                      || (providerRequiresVerificationField(formData.provider) && !formData.securityAnswer)
                    }
                    className="w-full"
                    data-testid="connect-import-btn"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 size={20} className="animate-spin" />
                        Connecting to {selectedProvider.name}...
                      </>
                    ) : (
                      <>
                        Connect & Import Report
                        <ArrowRight size={20} />
                      </>
                    )}
                  </OnboardingButton>
                </motion.div>

                {/* Secondary Actions */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="mt-4 flex flex-col items-center gap-3"
                >
                  {/* Manual Upload Option */}
                  <button
                    onClick={() => setShowManualUpload(!showManualUpload)}
                    className="text-slate-400 hover:text-slate-200 text-sm transition-colors inline-flex items-center gap-1.5"
                    data-testid="toggle-manual-upload"
                  >
                    <Upload size={14} />
                    {showManualUpload ? 'Hide manual upload' : 'Or upload your report manually'}
                  </button>
                  
                  {/* Create Account Link */}
                  {selectedProvider.create_account_url && (
                    <a
                      href={selectedProvider.create_account_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-500 hover:text-emerald-400 text-xs transition-colors inline-flex items-center gap-1"
                      data-testid="create-account-link"
                    >
                      {selectedProvider.create_account_cta || `Don't have a ${selectedProvider.name} account? Create one`}
                      <ExternalLink size={12} />
                    </a>
                  )}

                  <button
                    type="button"
                    onClick={handleImportLater}
                    className="text-slate-400 hover:text-slate-200 text-sm transition-colors"
                    data-testid="import-later-btn"
                  >
                    Import Later
                  </button>
                </motion.div>

                {/* Manual Upload Section */}
                <AnimatePresence>
                  {showManualUpload && (
                    <ManualUploadSection 
                      onFileSelect={handleFileSelect}
                      isUploading={isUploading}
                    />
                  )}
                </AnimatePresence>

                {/* Back Navigation */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.55 }}
                  className="mt-6 pt-6 border-t border-slate-700/30"
                >
                  <OnboardingButton
                    onClick={handleBackToProvider}
                    variant="ghost"
                    className="text-slate-400 hover:text-white"
                  >
                    <ArrowLeft size={18} />
                    Change Provider
                  </OnboardingButton>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </OnboardingCard>

      {/* Import Failure Modal */}
      <ImportFailureModal
        isOpen={showFailureModal}
        onClose={() => setShowFailureModal(false)}
        onRetry={handleRetry}
        onUpdateCredentials={handleUpdateCredentials}
        onManualUpload={handleManualUploadClick}
        onSkipToHome={handleSkipToHome}
        errorDetails={importError}
      />
    </>
  );
};

export default UnifiedReportImportStep;
